import type {
  IContentPage,
  IContentPageFields
} from '~~/lib/ContentfulService';
import { isEntryOfContentType } from '~/utils/guards/isEntryOfContentType';
import type { Entry } from 'contentful';
import type { IPaywallExtended } from '~~/lib/Contentful/model';
import { groupSections } from '~/utils/groupSections';
import { isLeadPaywall } from '~/utils/paywall/isLeadPaywall';

/**
 * Content that stays outside the paywall preview after
 * {@link moveAllEntriesAfterPaywallIntoPaywall} (everything before the teaser section).
 */
export function getVisibleContentSectionBeforePaywall(
  contentEntries: IContentPage['fields']['contentSection']
): NonNullable<IContentPage['fields']['contentSection']> {
  const entries = contentEntries ?? [];
  const paywallIndex = entries.findIndex((contentEntry) =>
    isEntryOfContentType(contentEntry, 'paywall')
  );

  if (paywallIndex < 0) {
    return entries;
  }

  const precedingGroups = groupSections(
    entries.slice(0, paywallIndex) as Entry<unknown>[]
  );
  const activeGroup = precedingGroups.at(-1);
  const precedingEntry = entries[paywallIndex - 1];
  const activeSection = isEntryOfContentType(precedingEntry, 'section')
    ? precedingEntry
    : activeGroup?.type === 'sectionEntry'
      ? activeGroup.section
      : undefined;
  const start = activeSection ? entries.indexOf(activeSection) : paywallIndex;

  return entries.slice(0, start);
}

export function moveAllEntriesAfterPaywallIntoPaywall(
  entry: IContentPage
): void {
  const contentEntries = entry.fields?.contentSection ?? [];
  const paywallIndex = contentEntries.findIndex((contentEntry) =>
    isEntryOfContentType(contentEntry, 'paywall')
  );

  if (paywallIndex < 0) {
    return;
  }

  const paywall = contentEntries[paywallIndex] as IPaywallExtended;
  if (isLeadPaywall(paywall)) {
    return;
  }

  const visible = getVisibleContentSectionBeforePaywall(contentEntries);
  const activeSectionIndex = visible.length;

  const entries = [
    ...contentEntries.slice(activeSectionIndex, paywallIndex),
    ...contentEntries.slice(paywallIndex + 1)
  ] as Entry<unknown>[];

  entry.fields.contentSection = [
    ...visible,
    paywall
  ] as IContentPageFields['contentSection'];

  paywall['extension'] = {
    nextEntries: entries
  };
}
